// =============================================================================
// File: frontend/src/components/SubscriptionGate.jsx
// Full-screen HARD-STOP gate shown after login when the user has no active
// subscription. Two modes:
//   - NEW (status "trial"/none, never started): "Start your 7-day free trial"
//   - LAPSED (status "expired"/"canceled"): "Your trial has ended" + grace-period
//     purge notice (data deleted after the grace window unless they subscribe).
// Card-on-file trial checkout reuses /api/billing/create-checkout-session.
// Patent claims exercised: P-007 Claims 1-6
// =============================================================================

import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config';

const GRACE_DAYS = 7;  // days after trial end before account + data are purged

function SubscriptionGate({ user, token, onLogout }) {
  const [tiers, setTiers] = useState([]);
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/billing/plans`)
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (data?.tiers) setTiers(data.tiers); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const lapsed = ['expired', 'canceled', 'cancelled'].includes(user?.subscription_status);

  const formatDate = (ts) => {
    if (!ts) return null;
    const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Purge date = trial end + grace window (display only; the purge job enforces it).
  const purgeDate = (() => {
    if (!lapsed || !user?.trial_ends_at) return null;
    const end = new Date(user.trial_ends_at);
    end.setDate(end.getDate() + GRACE_DAYS);
    return formatDate(end);
  })();

  const handleStart = async (tier) => {
    const priceId = billingInterval === 'monthly' ? tier.monthly_price_id : tier.annual_price_id;
    if (!priceId) return;
    setActionLoading(true);
    setError(null);
    try {
      const referral = (() => {
        try { return JSON.parse(sessionStorage.getItem('healist_referral'))?.code; } catch { return null; }
      })();
      const res = await fetch(`${API_BASE}/api/billing/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(referral ? { price_id: priceId, referral_code: referral } : { price_id: priceId }),
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.checkout_url;
      } else {
        const errData = await res.json().catch(() => ({}));
        setError(errData.detail || 'Could not start checkout. Please try again.');
      }
    } catch (err) {
      setError('Could not connect to the payment service. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="subgate-overlay">
      <div className="subgate-inner">
        <div className="subgate-brand">
          <div className="subgate-logo">H</div>
          <span className="subgate-brandName">Healist.AI</span>
        </div>

        <h1 className="subgate-title">
          {lapsed ? 'Your trial has ended' : 'Start your 7-day free trial'}
        </h1>
        <p className="subgate-subtitle">
          {lapsed
            ? 'Pick a plan to continue using Healist. Your data is safe and waiting.'
            : 'Choose a plan to begin. You won’t be charged until your trial ends, and you can cancel anytime.'}
        </p>

        {lapsed && (
          <div className="subgate-purgeNotice">
            {purgeDate
              ? <>To meet privacy regulations, if you don’t subscribe by <strong>{purgeDate}</strong> your
                 account and all associated data will be permanently deleted. We’ll email you to confirm.</>
              : <>To meet privacy regulations, if you don’t subscribe within {GRACE_DAYS} days your account and
                 all associated data will be permanently deleted. We’ll email you to confirm.</>}
          </div>
        )}

        {error && <div className="subgate-error">{typeof error === 'object' ? JSON.stringify(error) : error}</div>}

        <div className="sub-billingToggle subgate-toggle">
          <button
            className={`sub-toggleBtn ${billingInterval === 'monthly' ? 'sub-toggleBtn--active' : ''}`}
            onClick={() => setBillingInterval('monthly')}
          >
            Monthly
          </button>
          <button
            className={`sub-toggleBtn ${billingInterval === 'annual' ? 'sub-toggleBtn--active' : ''}`}
            onClick={() => setBillingInterval('annual')}
          >
            Annual
            <span className="sub-savingsBadge">Save 10%</span>
          </button>
        </div>

        {loading ? (
          <div className="subgate-loading">Loading plans…</div>
        ) : (
          <div className="sub-tierGrid subgate-grid">
            {tiers.map((tier) => (
              <div key={tier.key} className={`sub-tierCard${tier.popular ? ' sub-tierCard--recommended' : ''}`}>
                {tier.popular && <div className="sub-popularBadge">Most Popular</div>}
                <div className="sub-tierCardHeader">
                  <h3 className="sub-tierName">{tier.name}</h3>
                  <div className="sub-tierPrice">
                    <span className="sub-priceAmount">
                      ${billingInterval === 'monthly' ? tier.monthly_price.toFixed(2) : tier.annual_monthly_price.toFixed(2)}
                    </span>
                    <span className="sub-priceInterval">/mo</span>
                  </div>
                  {billingInterval === 'annual' && (
                    <div className="sub-annualTotal">${tier.annual_price.toFixed(2)}/year (billed annually)</div>
                  )}
                  <div className="sub-trialNote">{tier.trial_days}-day free trial &middot; Cancel anytime</div>
                </div>
                <ul className="sub-featureList">
                  {tier.features.map((f, i) => (
                    <li key={i} className="sub-featureItem"><span className="sub-featureCheck">&#10003;</span>{f}</li>
                  ))}
                  {(tier.not_included || []).map((f, i) => (
                    <li key={`x${i}`} className="sub-featureItem sub-featureItem--excluded">
                      <span className="sub-featureCheck sub-featureCheck--x">&#10007;</span>{f}
                    </li>
                  ))}
                </ul>
                <div className="sub-tierCardFooter">
                  <button
                    className={`btn ${tier.popular ? 'btn-primary' : 'btn-secondary'} sub-upgradeBtn`}
                    onClick={() => handleStart(tier)}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Processing…' : (lapsed ? 'Subscribe' : 'Start Free Trial')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="subgate-footnote">
          Signed in as {user?.email}. Need your data or to close your account? Email{' '}
          <a href="mailto:support@healist.ai">support@healist.ai</a>.{' '}
          <button type="button" className="subgate-logout" onClick={onLogout}>Sign out</button>
        </p>
      </div>
    </div>
  );
}

export default SubscriptionGate;
